
/**這個Adapter已經使用了autoThrowErrorFetch，不需要在使用時另外加上autoThrowError了 */
import { useContext, useEffect } from "react";
import { API_Version, ApiVersionInfo, ResultObj, getControllerApiVersion, autoThrowErrorFetch } from "../Domain";
import { ApiContext, useApi } from "../apiProvider";
import { NotificationTargetSearchResItem } from "../controller/NotificationTS";
import { defaultDateTimeToDotNet6Format } from "../function";


// BulletinApi的Hooks使用方式
export const useBulletinApi = () => {
    const apiManagerName = "useBulletinApiManager";
    const context = useContext(ApiContext);
    if (!context) {
        throw new Error("useBulletinApi must be used within a ApiProvider");
    }

    let { apiManagers, registerApiManager } = context;
    useEffect(() => {
        if (!apiManagers[apiManagerName]) {
            const newBulletinApiManager = new BulletinApiManager();
            newBulletinApiManager.init().then((apiManager) => {
                registerApiManager(apiManagerName, apiManager);
            }).catch((e) => {
                console.error("Error initializing API Manager", e);
            });
        }
    }, [apiManagers[apiManagerName]]);

    return useApi<BulletinApiManager>(apiManagerName);
};

// BulletinApiInterface 的定義
export interface BulletinApiInterface {
    /** 新增公佈欄 */
    BulletinCreate: (obj: BulletinCreateParams) => Promise<ResultObj>
    /** 修改公佈欄 */
    BulletinUpdate: (obj: BulletinUpdateParams) => Promise<ResultObj>
    /** 查詢公佈欄細項 */
    BulletinDetail: (obj: BulletinDetailParams) => Promise<ResultObj<BulletinDetailResItem>>
    /** 查詢公佈欄列表（用於小鈴鐺） */
    BulletinSearch: (obj: BulletinSearchParams) => Promise<ResultObj<BulletinSearchResItem>>
    /** 查詢公佈欄列表（用於編輯區） */
    BulletinEditSearch: (obj: BulletinEditSearchParams) => Promise<ResultObj<BulletinEditSearchResItem[]>>
    /** 公佈欄訊息紀錄已讀 */
    BulletinRead: (obj: BulletinReadParams) => Promise<ResultObj>
    /** 查詢已讀的公佈欄訊息列表 */
    BulletinReadSearch: (obj: BulletinReadSearchParams) => Promise<ResultObj<BulletinReadSearchResItem[]>>
    /**公佈欄通知目標修改 */
    BulletinTargetUpdate: (obj: BulletinTargetUpdateParams) => Promise<ResultObj>
    /**公佈欄通知目標查詢 */
    BulletinTargetSearch: (obj: BulletinTargetSearchParams) => Promise<ResultObj<BulletinTargetSearchResItem[]>>
}

// BulletinApiManager 類別實現
export class BulletinApiManager implements BulletinApiInterface {

    private adapter: BulletinDotnet6Adapter | BulletinFrameworkAdapter | null = null;
    private version: keyof typeof API_Version | undefined;

    constructor(version?: keyof typeof API_Version) {
        this.version = version;
    }

    async init(): Promise<BulletinApiManager> {
        let target_version: string | undefined = this.version;
        if (target_version === undefined) {
            let getAPIversionResult: ApiVersionInfo | null = await getControllerApiVersion("Bulletin");
            target_version = getAPIversionResult === null ? this.version : getAPIversionResult.version;
        }

        if (target_version === "dotNet6") this.adapter = new BulletinDotnet6Adapter();
        else if (target_version === 'dotNet6_AM_N') this.adapter = new BulletinDotnet6Adapter();//預設是 dotNet6，有需要才再另寫Adapter
        else if (target_version === "framework") this.adapter = new BulletinFrameworkAdapter();

        if (this.adapter !== null) {
            this.BulletinCreate = this.adapter.BulletinCreate;
            this.BulletinUpdate = this.adapter.BulletinUpdate;
            this.BulletinDetail = this.adapter.BulletinDetail;
            this.BulletinSearch = this.adapter.BulletinSearch;
            this.BulletinEditSearch = this.adapter.BulletinEditSearch;
            this.BulletinRead = this.adapter.BulletinRead;
            this.BulletinReadSearch = this.adapter.BulletinReadSearch;
            this.BulletinTargetUpdate = this.adapter.BulletinTargetUpdate;
            this.BulletinTargetSearch = this.adapter.BulletinTargetSearch;
        }
        return this;
    }

    emptyAdapter = async (): Promise<ResultObj> => ({ success: false, message: 'API未實現[BulletinAdapter]', data: null });

    BulletinCreate = async (obj: BulletinCreateParams): Promise<ResultObj> => await this.emptyAdapter();
    BulletinUpdate = async (obj: BulletinUpdateParams): Promise<ResultObj> => await this.emptyAdapter();
    BulletinDetail = async (obj: BulletinDetailParams): Promise<ResultObj<BulletinDetailResItem>> => await this.emptyAdapter();
    BulletinSearch = async (obj: BulletinSearchParams): Promise<ResultObj<BulletinSearchResItem>> => await this.emptyAdapter();
    BulletinEditSearch = async (obj: BulletinEditSearchParams): Promise<ResultObj<BulletinEditSearchResItem[]>> => await this.emptyAdapter();
    BulletinRead = async (obj: BulletinReadParams): Promise<ResultObj> => await this.emptyAdapter();
    BulletinReadSearch = async (obj: BulletinReadSearchParams): Promise<ResultObj<BulletinReadSearchResItem[]>> => await this.emptyAdapter();
    BulletinTargetUpdate = async (obj: BulletinTargetUpdateParams): Promise<ResultObj> => await this.emptyAdapter();
    BulletinTargetSearch = async (obj: BulletinTargetSearchParams): Promise<ResultObj<BulletinTargetSearchResItem[]>> => await this.emptyAdapter();
}

class BulletinBaseAdapter implements BulletinApiInterface {
    BulletinCreate = async (obj: BulletinCreateParams): Promise<ResultObj> => await autoThrowErrorFetch('Bulletin/BulletinCreate', obj);
    BulletinUpdate = async (obj: BulletinUpdateParams): Promise<ResultObj> => await autoThrowErrorFetch('Bulletin/BulletinUpdate', obj);
    BulletinDetail = async (obj: BulletinDetailParams): Promise<ResultObj<BulletinDetailResItem>> => await autoThrowErrorFetch('Bulletin/BulletinDetail', obj);
    BulletinSearch = async (obj: BulletinSearchParams): Promise<ResultObj<BulletinSearchResItem>> => await autoThrowErrorFetch('Bulletin/BulletinSearch', obj);
    BulletinEditSearch = async (obj: BulletinEditSearchParams): Promise<ResultObj<BulletinEditSearchResItem[]>> => await autoThrowErrorFetch('Bulletin/BulletinEditSearch', obj);
    BulletinRead = async (obj: BulletinReadParams): Promise<ResultObj> => await autoThrowErrorFetch('Bulletin/BulletinRead', obj);
    BulletinReadSearch = async (obj: BulletinReadSearchParams): Promise<ResultObj<BulletinReadSearchResItem[]>> => await autoThrowErrorFetch('Bulletin/BulletinReadSearch', obj);
    BulletinTargetUpdate = async (obj: BulletinTargetUpdateParams): Promise<ResultObj> => await autoThrowErrorFetch('Bulletin/BulletinTargetUpdate', obj);
    BulletinTargetSearch = async (obj: BulletinTargetSearchParams): Promise<ResultObj<BulletinTargetSearchResItem[]>> => await autoThrowErrorFetch('Bulletin/BulletinTargetSearch', obj);
}

class BulletinDotnet6Adapter extends BulletinBaseAdapter {
    BulletinCreate = async (obj: BulletinCreateParams): Promise<ResultObj> => await autoThrowErrorFetch('Bulletin/BulletinCreate', {
        ...obj,
        valid_date: defaultDateTimeToDotNet6Format(obj.valid_date),
        invalid_date: defaultDateTimeToDotNet6Format(obj.invalid_date)
    });
    BulletinUpdate = async (obj: BulletinUpdateParams): Promise<ResultObj> => await autoThrowErrorFetch('Bulletin/BulletinUpdate', {
        ...obj,
        valid_date: defaultDateTimeToDotNet6Format(obj.valid_date),
        invalid_date: defaultDateTimeToDotNet6Format(obj.invalid_date)
    });
    BulletinEditSearch = async (obj: BulletinEditSearchParams): Promise<ResultObj<BulletinEditSearchResItem[]>> => await autoThrowErrorFetch('Bulletin/BulletinEditSearch', {
        ...obj,
        valid_date_start: defaultDateTimeToDotNet6Format(obj.valid_date_start),
        valid_date_end: defaultDateTimeToDotNet6Format(obj.valid_date_end),
        invalid_date_start: defaultDateTimeToDotNet6Format(obj.invalid_date_start),
        invalid_date_end: defaultDateTimeToDotNet6Format(obj.invalid_date_end),
    });
    BulletinSearch = async (obj: BulletinSearchParams): Promise<ResultObj<BulletinSearchResItem>> => await autoThrowErrorFetch('Bulletin/BulletinSearch', {
        ...obj,
        valid_date_start: defaultDateTimeToDotNet6Format(obj.valid_date_start),
        valid_date_end: defaultDateTimeToDotNet6Format(obj.valid_date_end),
    });
}

class BulletinFrameworkAdapter extends BulletinBaseAdapter {
    BulletinDetail = async (obj: BulletinDetailParams): Promise<ResultObj<BulletinDetailResItem>> => await autoThrowErrorFetch('Bulletin/BulletinDetail', obj).then(res => {
        return {
            ...res,
            data: res.data && res.data.length > 0 ? res.data[0] : {}
        }
    });
    BulletinTargetUpdate = async (obj: BulletinTargetUpdateParams): Promise<ResultObj> => await autoThrowErrorFetch('Notification/NotificationTargetUpdate', { id: obj.bulletin_id, user_ids: obj.user_ids, ul_ids: obj.ul_ids, ud_ids: obj.ud_ids });
    BulletinTargetSearch = async (obj: BulletinTargetSearchParams): Promise<ResultObj<BulletinTargetSearchResItem[]>> =>
        await autoThrowErrorFetch('Notification/NotificationTargetSearch', { id: obj.bulletin_id }).then(res => {
            return {
                ...res,
                data: !!!res.data || res.data.map((item: NotificationTargetSearchResItem) => ({
                    ...item,
                    bt_id: parseInt(item.nt_id),
                }))
            }
        })
}

/**
 * @description 新增公佈欄（參數）
 * @param {string} subject 主題
 * @param {string} content 內容
 * @param {string} valid_date 生效日期
 * @param {string} invalid_date 失效日期
 * @param {string} is_top 是否置頂
 * @param {string} is_login_read 是否需登入後已讀
 */
export interface BulletinCreateParams {
    subject: string;
    content: string;
    valid_date?: string;
    invalid_date?: string;
    is_top: string;
    is_login_read: string;
}

/**
 * @description 修改公佈欄（參數）
 * @param {string} bulletin_id 公佈欄ID
 * @param {string} subject 主題
 * @param {string} content 內容
 * @param {string} valid_date 生效日期
 * @param {string} invalid_date 失效日期
 * @param {string} is_top 是否置頂
 * @param {string} visible 是否可見
 * @param {string} is_login_read 是否需登入後已讀
 */
export interface BulletinUpdateParams {
    bulletin_id: string;
    subject?: string;
    content?: string;
    valid_date?: string;
    invalid_date?: string;
    is_top?: string;
    visible: string;
    is_login_read?: string;
}

/**
 * @description 查詢公佈欄細項（參數）
 * @param {string} bulletin_id 公佈欄ID
 */
export interface BulletinDetailParams {
    bulletin_id: string;
}

/**
 * @description 查詢公佈欄細項（回傳）
 * @param {string} bulletin_id 公佈欄ID
 * @param {string} subject 主題
 * @param {string} content 內容
 * @param {string} valid_date 生效日期
 * @param {string} invalid_date 失效日期
 * @param {string} is_top 是否置頂
 * @param {string} visible 是否可見
 * @param {string} is_login_read 是否需登入後已讀
 */
export interface BulletinDetailResItem {
    bulletin_id: string;
    subject: string;
    content: string;
    valid_date: string;
    invalid_date: string;
    is_top: string;
    visible: string;
    is_login_read: string;
}


/**
 * @description 查詢公佈欄列表（參數）
 * @param {string} is_login_read 是否需登入後已讀
 * @param {string} is_read 是否已讀
 * @param {number} page 當前頁碼
 * @param {number} num_per_page 每頁顯示數量
 * @param {string} subject 主題
 * @param {string} valid_date_start 生效日期（開始）
 * @param {string} valid_date_end 生效日期（結束）
 * @param {string} invalid_date_start 失效日期（開始）
 * @param {string} invalid_date_end 失效日期（結束）
 */
export interface BulletinSearchParams {
    is_login_read?: string;
    is_read?: string;
    page?: number;
    num_per_page?: number;
    subject?: string;
    valid_date_start?: string;
    valid_date_end?: string;
    invalid_date_start?: string;
    invalid_date_end?: string;
}

/**
 * @description 公佈欄訊息物件
 * @param {string} bulletin_id 公佈欄ID
 * @param {string} subject 主題
 * @param {string} content 內容
 * @param {string} valid_date 生效日期
 * @param {string} invalid_date 失效日期
 * @param {string} is_top 是否置頂
 * @param {string} visible 是否可見
 * @param {string} is_login_read 是否需登入後已讀
 * @param {string} latest_upd_time 最後更新時間
 * @param {string} is_read 是否已讀
 */
export interface BulletinSearchResItemBulletin {
    bulletin_id: string;
    subject: string;
    content: string;
    valid_date: string;
    invalid_date: string;
    is_top: string;
    visible: string;
    is_login_read: string;
    latest_upd_time: string;
    is_read: string;
}

/**
 * @description 查詢公佈欄列表（回傳）
 * @param {number} read_count 已讀數量
 * @param {number} unread_count 未讀數量
 * @param {BulletinSearchResItemBulletin[]} bulletins 公佈欄列表
 */
export interface BulletinSearchResItem {
    read_count: number;
    unread_count: number;
    bulletins: BulletinSearchResItemBulletin[]
}

/**
 * @description 公佈欄紀錄已讀（參數）
 * @param {string[]} bulletin_ids 公佈欄ID列表
 */
export interface BulletinReadParams {
    bulletin_ids: string[];
}

/**
 * @description 查詢公佈欄已讀列表（參數）
 * @param {string} bulletin_id 公佈欄ID
 */
export interface BulletinReadSearchParams {
    bulletin_id: string;
}

/**
 * @description 查詢公佈欄已讀列表（回傳）
 * @param {string} user_id 帳號ID
 * @param {string} username 帳號
 * @param {string} name 姓名
 * @param {string} ul_id 職務ID
 * @param {string} ul_name 職務名稱
 * @param {string} read_time 已讀時間
 */
export interface BulletinReadSearchResItem {
    user_id: string;
    username: string;
    name: string;
    ul_id: string;
    ul_name: string;
    read_time: string;
}

/**
 * @description 查詢公佈欄編輯列表（參數）
 * @param {string} subject 主題
 * @param {string} valid_date_start 生效日期（開始）
 * @param {string} valid_date_end 生效日期（結束）
 * @param {string} invalid_date_start 失效日期（開始）
 * @param {string} invalid_date_end 失效日期（結束）
 * @param {string} visible 是否可見
 * @param {string} is_top 是否置頂
 * @param {string} is_login_read 是否需登入後已讀
 * @param {number} page 當前頁碼
 * @param {number} num_per_page 每頁顯示數量
 */
export interface BulletinEditSearchParams {
    subject?: string;
    valid_date_start?: string;
    valid_date_end?: string;
    invalid_date_start?: string;
    invalid_date_end?: string;
    visible?: string;
    is_top?: string;
    is_login_read?: string;
    page?: number;
    num_per_page?: number;
}

/**
 * @description 查詢公佈欄編輯列表（回傳）
 * @param {string} bulletin_id 公佈欄ID
 * @param {string} subject 主題
 * @param {string} content 內容
 * @param {string} valid_date 生效日期
 * @param {string} invalid_date 失效日期
 * @param {string} is_top 是否置頂
 * @param {string} visible 是否可見
 * @param {string} is_login_read 是否需登入後已讀
 * @param {number} target_count 目標人數
 * @param {number} read_count 已讀人數
 */
export interface BulletinEditSearchResItem {
    bulletin_id: string;
    subject: string;
    content: string;
    valid_date: string;
    invalid_date: string;
    is_top: string;
    visible: string;
    is_login_read: string;
    target_count: number;
    read_count: number;
}


/**
 * @description 公佈欄通知目標修改（參數）
 * @param {string} bulletin_id 公佈欄ID
 * @param {string[]} user_ids 帳號列表
 * @param {string[]} ul_ids 職務列表
 * @param {string[]} ud_ids 職責列表
 */
export interface BulletinTargetUpdateParams {
    bulletin_id: string;
    user_ids?: string[];
    ul_ids?: string[];
    ud_ids?: string[];
}

/**
 * @description 公佈欄通知目標修改（參數）
 * @param {string} bulletin_id 公佈欄ID
 */
export interface BulletinTargetSearchParams {
    bulletin_id: string;
}

/**
 * @description 公佈欄通知目標查詢（回傳）
 * @param {number} bt_id 公佈欄目標ID
 * @param {string} bulletin_id 公佈欄ID
 * @param {string} user_id 帳號ID
 * @param {string} username 帳號
 * @param {string} name 姓名
 * @param {number} user_ul_id 帳號所屬職務ID
 * @param {string} user_ul_code 帳號所屬職務代碼
 * @param {string} user_ul_name 帳號所屬職務名稱
 * @param {number} ul_id 職務ID
 * @param {string} ul_code 職務代碼
 * @param {string} ul_name 職務名稱
 * @param {number} ud_id 職責ID
 * @param {string} ud_code 職責代碼
 * @param {string} ud_name 職責名稱
 */
export interface BulletinTargetSearchResItem {
    bt_id: number;
    bulletin_id: string;
    user_id: string;
    username: string;
    name: string;
    user_ul_id: number;
    user_ul_code: string;
    user_ul_name: string;
    ul_id: number;
    ul_code: string;
    ul_name: string;
    ud_id: number;
    ud_code: string;
    ud_name: string;
}